/**
 * Leviathan Web - Authentication Context
 * Quasar Prime LLC
 *
 * Manages user authentication state, login, and logout.
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { parseApiError } from '@/utils/errorHandler';
import { API_URL } from '@/config/api';
import { startRefreshTimer, stopRefreshTimer } from '@/utils/api';

interface User {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  tenantId: string;
  tenantName: string;
}

interface AuthContextType {
  user: User | null;
  accessToken: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for stored tokens on mount
    const storedToken = localStorage.getItem('accessToken');

    if (storedToken) {
      setAccessToken(storedToken);
      startRefreshTimer();
      fetchCurrentUser(storedToken);
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchCurrentUser = async (token: string) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUser({
          ...data.user,
          tenantName: data.user.tenant.name,
        });
      } else {
        // Token invalid, try refresh
        await refreshAccessToken();
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setIsLoading(false);
    }
  };

  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        logout();
        return;
      }

      const response = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        setAccessToken(data.accessToken);
        startRefreshTimer();
        await fetchCurrentUser(data.accessToken);
      } else {
        logout();
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new Error(errorMessage);
      }

      const data = await response.json();

      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);

      setAccessToken(data.accessToken);
      setUser(data.user);
      startRefreshTimer();
    } catch (error) {
      console.error('Login error:', error);
      // Re-throw with user-friendly message for network errors
      if (error instanceof Error && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to server. Please check if the API is running.');
      }
      throw error;
    }
  };

  // `register` used to live here, calling POST /api/auth/register. That endpoint
  // was public and self-provisioned an ADMIN account, and has been removed.
  // Users are added by admin-issued invitation; the invitee's account is created
  // by AcceptInvitePage posting to /api/settings/invitations/accept, which does
  // not return a session — the invitee then signs in normally.

  const logout = () => {
    stopRefreshTimer();
    // Call logout endpoint (fire and forget)
    if (accessToken) {
      fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }).catch(console.error);
    }

    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setAccessToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        login,
        logout,
        isAuthenticated: !!user,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
